import { gql } from '@apollo/client';
import { call, delay, put, select } from 'redux-saga/effects';

import { selectConfig } from '@app/redux/config';

import { Locale, Platform, Query, TokenType } from '@mfe/shared/schema-types';

import { logout } from '../../events';
import { graphqlQuery } from '../../graphql';
import { selectUser } from '../authUserSlice';
import { verifyTokenInfo } from './logInSagas';
import { MS_IN_A_MINUTE, parseTokenInfoCookie } from '../utils';

export const GET_TOKEN_USING_REFRESH = gql`
  query getTokenUsingRefresh($input: GetTokenUsingRefreshInput!) {
    getTokenUsingRefresh(input: $input) {
      accessToken
      accessTokenExpirationTime
      idToken
      refreshToken
    }
  }
`;

export function* tryToRefresh(payload: { refreshToken: string; platform: Platform; locale?: Locale }) {
  const { refreshToken, platform, locale } = payload;

  const data: Query = yield call(graphqlQuery, {
    query: GET_TOKEN_USING_REFRESH,
    variables: { input: { refreshToken, platform, locale } },
  });

  const accessToken = data.getTokenUsingRefresh.accessToken;
  const accessTokenExpirationTime = data.getTokenUsingRefresh.accessTokenExpirationTime;
  const idToken = data.getTokenUsingRefresh.idToken;
  const newRefreshToken = data.getTokenUsingRefresh.refreshToken;

  return {
    accessToken,
    accessTokenExpirationTime,
    idToken,
    refreshToken: newRefreshToken,
  };
}

export function* renewToken(payload: { payload: { refreshToken: string; tokenType?: TokenType; locale?: Locale } }) {
  const { refreshToken, locale } = payload.payload;

  const { platform }: ReturnType<typeof selectConfig> = yield select(selectConfig);
  try {
    if (!refreshToken) {
      console.info('No refresh token');
      yield put(logout());
      return;
    }
    const {
      accessToken,
      accessTokenExpirationTime,
      idToken,
      refreshToken: newRefreshToken,
    } = yield call(tryToRefresh, { refreshToken, platform, locale });
    const tokenInfo = {
      accessToken,
      refreshToken: newRefreshToken ?? refreshToken,
      accessTokenExpirationTime,
      idToken,
    };
    yield call(verifyTokenInfo, {
      type: 'verifyTokenInfo',
      payload: {
        tokenInfo,
      },
    });
  } catch (err) {
    console.info(`failed to refresh token with err: ${err}`);
    yield put(logout());
    return;
  }
}

export function* refreshCountdown(payload: { type: string; payload: { stringifiedTokenInfo: string } }) {
  const stringifiedTokenInfo = payload.payload.stringifiedTokenInfo;
  const tokenInfo = parseTokenInfoCookie(stringifiedTokenInfo);
  if (!tokenInfo) {
    console.info('tokenInfo cookie not found or deformed');
    yield put(logout());
    return;
  }
  const { accessTokenExpirationTime } = tokenInfo;
  const timeToRefreshMS = new Date(accessTokenExpirationTime).getTime() - new Date().getTime() - 5 * MS_IN_A_MINUTE;

  yield delay(timeToRefreshMS);

  // Okta rotates refresh tokens on use. Re-read tokenInfo from redux after the
  // delay so we use the latest refresh token and avoid `invalid_refresh_token`.
  const {
    user: { auth },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const { refreshToken, type: tokenType, locale } = auth.tokenInfo ?? tokenInfo;

  if (!refreshToken) {
    console.info('No refresh token');
    yield put(logout());
    return;
  }

  yield call(renewToken, {
    payload: {
      refreshToken,
      tokenType,
      locale,
    },
  });
}
